import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { api } from '../App';
import { BookOpen, LogOut, Users, Building2, TrendingUp, Search, Lightbulb, BarChart3 } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316'];

export default function HRDashboard({ user, logout, setUser }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [companyAnalytics, setCompanyAnalytics] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchCompanyAnalytics();
  }, []);

  const fetchCompanyAnalytics = async () => {
    try {
      const response = await api.get('/hr/analytics/company');
      setCompanyAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Şirket analizleri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const pieChartData = companyAnalytics?.overall_distribution?.distribution?.map(item => ({
    name: item.name,
    value: item.count,
    percentage: item.percentage
  })) || [];

  const deptComparisonData = companyAnalytics?.department_breakdown?.map(dept => ({
    name: dept.department,
    total: dept.employee_count,
    completed: dept.distribution.reduce((sum, d) => sum + d.count, 0)
  })) || [];

  const handleStopImpersonation = async () => {
    try {
      const response = await api.post('/impersonate/stop');
      
      const { access_token, user: adminUser } = response.data;
      localStorage.setItem('access_token', access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(adminUser);
      
      toast.success('Süper admin hesabınıza döndünüz');
      navigate('/');
    } catch (error) {
      console.error('Stop impersonation failed:', error);
      toast.error('Oturum sonlandırılamadı');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-rose-600 to-pink-600 rounded-xl flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-800">Mizaç360</h1>
              <p className="text-sm text-slate-600">İK Paneli</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {user.impersonation?.active && (
              <Badge className="bg-orange-100 text-orange-700 animate-pulse">
                Taklit Modu: {user.impersonation.admin_name}
              </Badge>
            )}
            <Badge className="bg-rose-100 text-rose-700">İnsan Kaynakları</Badge>
            <span className="text-sm text-slate-600">{user.name}</span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/coach')}
              className="bg-gradient-to-r from-blue-50 to-emerald-50 border-blue-200 hover:bg-blue-100"
            >
              💬 AI Koç
            </Button>
            {user.impersonation?.active ? (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleStopImpersonation}
                className="bg-orange-50 border-orange-200"
              >
                Taklidi Durdur
              </Button>
            ) : null}
            <Button variant="outline" size="sm" onClick={logout}>
              <LogOut className="w-4 h-4 mr-2" />
              Çıkış
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-slate-800 mb-2">İK Analytics</h2>
          <p className="text-slate-600">Şirket genelinde mizaç analizleri ve stratejik İK önerileri</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600"></div>
          </div>
        ) : (
          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="overview">Genel Bakış</TabsTrigger>
              <TabsTrigger value="departments">Departmanlar</TabsTrigger>
              <TabsTrigger value="employees">Çalışanlar</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              {/* Stats */}
              <div className="grid md:grid-cols-4 gap-6">
                <Card className="shadow-lg border-none">
                  <CardHeader className="pb-3">
                    <CardDescription className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Toplam Çalışan
                    </CardDescription>
                    <CardTitle className="text-3xl">{companyAnalytics?.total_employees || 0}</CardTitle>
                  </CardHeader>
                </Card>
                <Card className="shadow-lg border-none">
                  <CardHeader className="pb-3">
                    <CardDescription className="flex items-center gap-2">
                      <BarChart3 className="w-4 h-4" />
                      Tamamlanan
                    </CardDescription>
                    <CardTitle className="text-3xl text-emerald-600">
                      {companyAnalytics?.overall_distribution?.employees_with_reports || 0}
                    </CardTitle>
                  </CardHeader>
                </Card>
                <Card className="shadow-lg border-none">
                  <CardHeader className="pb-3">
                    <CardDescription className="flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Departman
                    </CardDescription>
                    <CardTitle className="text-3xl text-blue-600">
                      {companyAnalytics?.department_breakdown?.length || 0}
                    </CardTitle>
                  </CardHeader>
                </Card>
                <Card className="shadow-lg border-none">
                  <CardHeader className="pb-3">
                    <CardDescription>Tamamlanma</CardDescription>
                    <CardTitle className="text-3xl text-purple-600">
                      {companyAnalytics?.total_employees > 0
                        ? Math.round((companyAnalytics.overall_distribution.employees_with_reports / companyAnalytics.total_employees) * 100)
                        : 0}%
                    </CardTitle>
                  </CardHeader>
                </Card>
              </div>

              {/* Charts */}
              {companyAnalytics?.overall_distribution?.employees_with_reports > 0 && (
                <div className="grid md:grid-cols-2 gap-6">
                  <Card className="shadow-lg border-none">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-blue-600" />
                        Şirket Geneli Mizaç Dağılımı
                      </CardTitle>
                      <CardDescription>Tüm çalışanların mizaç tiplerinin dağılımı</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={pieChartData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percentage }) => `${name.split(' / ')[0]}: ${percentage}%`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {pieChartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  <Card className="shadow-lg border-none">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-emerald-600" />
                        Departman Bazlı Katılım
                      </CardTitle>
                      <CardDescription>Her departmandaki envanter tamamlanma durumu</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={deptComparisonData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="total" fill="#94a3b8" name="Toplam" />
                          <Bar dataKey="completed" fill="#10b981" name="Tamamlanan" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* AI Insights */}
              {companyAnalytics?.insights && (
                <Card className="shadow-xl border-none">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Lightbulb className="w-5 h-5 text-yellow-600" />
                      Stratejik İK Önerileri
                    </CardTitle>
                    <CardDescription>AI destekli işe alım ve gelişim stratejileri</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-6 rounded-lg border border-yellow-200">
                      <p className="whitespace-pre-wrap text-slate-700 leading-relaxed">{companyAnalytics.insights}</p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Departments Tab */}
            <TabsContent value="departments" className="space-y-6">
              <Card className="shadow-lg border-none">
                <CardHeader>
                  <CardTitle>Departman Detayları</CardTitle>
                  <CardDescription>Tüm departmanların mizaç dağılımı özeti</CardDescription>
                </CardHeader>
                <CardContent>
                  {companyAnalytics?.department_breakdown && companyAnalytics.department_breakdown.length > 0 ? (
                    <div className="space-y-4">
                      {companyAnalytics.department_breakdown.map((dept, idx) => (
                        <Card key={idx} className="border shadow-sm">
                          <CardHeader>
                            <div className="flex justify-between items-start">
                              <div>
                                <CardTitle className="text-lg">{dept.department}</CardTitle>
                                <CardDescription>{dept.employee_count} çalışan</CardDescription>
                              </div>
                              <Button variant="outline" size="sm">
                                Detaylı Rapor
                              </Button>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className="flex flex-wrap gap-2">
                              {dept.distribution.map((d, didx) => (
                                <Badge key={didx} variant="outline">
                                  {d.name}: {d.count}
                                </Badge>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-center py-8">Henüz departman verisi bulunmuyor</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Employees Tab */}
            <TabsContent value="employees" className="space-y-6">
              <Card className="shadow-lg border-none">
                <CardHeader>
                  <CardTitle>Çalışan Arama</CardTitle>
                  <CardDescription>Çalışan mizaç raporlarını görüntüleyin</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-4">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Çalışan adı veya email ile ara..."
                        className="pl-10"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                    <Button className="bg-gradient-to-r from-blue-600 to-emerald-600">
                      Ara
                    </Button>
                  </div>
                  <div className="mt-6">
                    <p className="text-slate-500 text-center py-8">Arama yapmak için yukarıdaki kutucuğu kullanın</p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </main>
    </div>
  );
}
